import { addMonths, endOfMonth, startOfMonth, todayDateKey } from './calendarGrid'
import { formatAmount } from './format'
import { RESERVATION_STATUS_OPTIONS } from './reservationViewModel'
import type { VenueRevenueReservationItem, VenueRevenueStatus, VenueRevenueSummary } from './revenueTypes'

/**
 * 매출 현황(예약 축) 화면 공통 순수 함수(설계 §2) — 프리셋 계산·쿼리 파라미터 조립·안내 문구·서식 판정.
 * `settlementViewModel.ts`(회차 축)와 반드시 별도 파일로 둔다(설계 §2 자율 결정 1) — 두 축의 필터·타입을
 * 한 화면 코드가 섞어 쓰는 것을 "만들 수 있는 코드가 없음"으로 막는다(api-spec §0-① "두 축의 숫자를
 * 더하거나 비교하지 않는다").
 *
 * 🚨 이 EP(#792 §2)는 `preset` 파라미터가 없다 — 서버는 "이번 달" 개념을 모르므로(대시보드 EP §3만
 * 자체 판정한다), 클라이언트가 항상 구체적인 `from`/`to`로 변환해 보낸다(설계 §2 자율 결정 3).
 */

export type RevenuePreset = 'THIS_MONTH' | 'LAST_MONTH' | 'CUSTOM'

export const REVENUE_PRESET_OPTIONS: { value: RevenuePreset; label: string }[] = [
  { value: 'THIS_MONTH', label: '이번 달' },
  { value: 'LAST_MONTH', label: '지난 달' },
  { value: 'CUSTOM', label: '직접 지정' },
]

export interface RevenuePeriod {
  /** "yyyy-MM-dd" */
  from: string
  to: string
}

/**
 * "이번 달" 범위 — 예약 캘린더가 이미 검증한 `calendarGrid.ts`의 월 경계 연산을 그대로 재사용한다
 * (12월→1월 롤오버·2월 말일 계산을 새로 만들지 않는다).
 */
export function thisMonthPeriod(today: string = todayDateKey()): RevenuePeriod {
  const from = startOfMonth(today)
  return { from, to: endOfMonth(from) }
}

/** "지난 달" 범위 — 이번 달 1일에서 한 달 전으로 이동한 뒤 그 달의 시작·끝을 구한다. */
export function lastMonthPeriod(today: string = todayDateKey()): RevenuePeriod {
  const lastMonthStart = addMonths(startOfMonth(today), -1)
  return { from: lastMonthStart, to: endOfMonth(lastMonthStart) }
}

export interface RevenueListFilter extends RevenuePeriod {
  courtId: number | 'ALL'
  /** 빈 배열 = 서버 기본값(매출 대상 전 상태). */
  statuses: VenueRevenueStatus[]
}

export function defaultRevenueListFilter(today?: string): RevenueListFilter {
  return { ...thisMonthPeriod(today), courtId: 'ALL', statuses: [] }
}

/** `/revenues`가 받는 상태 4값 — `HELD`/`PENDING`을 보내면 400(api-spec §2-1). */
const REVENUE_ALLOWED_STATUSES: readonly VenueRevenueStatus[] = [
  'CONFIRMED',
  'CANCEL_REQUESTED',
  'CANCELED',
  'COMPLETED',
]

function isRevenueStatus(status: string): status is VenueRevenueStatus {
  return (REVENUE_ALLOWED_STATUSES as readonly string[]).includes(status)
}

/**
 * 상태 필터 선택지 — `RESERVATION_STATUS_OPTIONS`(6값, 예약 목록 화면)에서 이 EP가 받는 4값만 걸러
 * 재사용한다(설계 §2 자율 결정 6). 라벨 문자열을 새로 정의하지 않아 두 화면의 문구가 어긋날 위험이 없다.
 */
export const REVENUE_STATUS_OPTIONS: { value: VenueRevenueStatus; label: string }[] =
  RESERVATION_STATUS_OPTIONS.filter(
    (option): option is { value: VenueRevenueStatus; label: string } => isRevenueStatus(option.value)
  )

/** 목록·요약 조회 쿼리 파라미터 — `undefined` 필드는 axios가 자동으로 뺀다. */
export function revenueQueryParams(
  filter: RevenueListFilter,
  cursor?: string
): Record<string, string | number | undefined> {
  return {
    from: filter.from,
    to: filter.to,
    courtId: filter.courtId === 'ALL' ? undefined : filter.courtId,
    statuses: filter.statuses.length > 0 ? filter.statuses.join(',') : undefined,
    cursor,
  }
}

/**
 * api-spec §4 체크리스트 — "수수료 정보가 없는 예약 N건이 포함되어 있어요" 안내.
 * `feeMissingCount<=0`이면 렌더할 문구가 없다(`null`).
 */
export function feeMissingNoticeText(summary: Pick<VenueRevenueSummary, 'feeMissingCount'>): string | null {
  if (summary.feeMissingCount <= 0) return null
  return `수수료 정보가 없는 예약 ${summary.feeMissingCount.toLocaleString('ko-KR')}건이 포함되어 있어요.`
}

/** api-spec §0-⑤·§4 체크리스트 — 참고 표시 전용, 어떤 합계에도 포함되지 않는다는 안내. */
export function onsitePaidNoticeText(summary: Pick<VenueRevenueSummary, 'onsitePaidAmount'>): string | null {
  if (summary.onsitePaidAmount <= 0) return null
  return `현장 수납 ${formatAmount(summary.onsitePaidAmount)}(정산 대상 아님)`
}

/**
 * 목록의 "적용 수수료" 열 — `feeSnapshotMissing`이면 `'-'`(🚫 `0원` 금지, api-spec §4 체크리스트).
 * 요율은 `appliedFeeRatePercentLabel`을 그대로 붙인다(`Number()` 변환·반올림 금지, api-spec §3-②).
 */
export function revenueFeeCellText(
  item: Pick<
    VenueRevenueReservationItem,
    'feeSnapshotMissing' | 'appliedFeeAmount' | 'appliedFeeRatePercentLabel'
  >
): string {
  if (item.feeSnapshotMissing) return '-'
  return `${formatAmount(item.appliedFeeAmount)} (${item.appliedFeeRatePercentLabel})`
}

/**
 * 목록의 "예약 일시" 열 — 서버가 준 `useDate`/`startTime`/`endTime`을 그대로 조립한다. 🚫 `startsAt`
 * (UTC)을 클라이언트가 다시 KST로 변환하지 않는다(api-spec §2 "화면이 시간대를 다시 변환하지 말 것").
 */
export function revenueUseDateTimeText(
  item: Pick<VenueRevenueReservationItem, 'useDate' | 'startTime' | 'endTime'>
): string {
  return `${item.useDate.replaceAll('-', '.')} ${item.startTime}–${item.endTime}`
}

/**
 * 음수 금액을 강조하는 텍스트 톤 — `Math.max(0, x)`로 뭉개지 않고(api-spec §0-④) 색으로만 구분한다.
 * `netRevenueAmount`·`settlementReflectedAmount`처럼 음수가 될 수 있는 필드에만 쓴다.
 */
export function amountTextClass(amount: number): string {
  return amount < 0 ? 'text-danger-500' : 'text-ink-700'
}

/**
 * CSV 다운로드용 회차 범위 — 조회 중인 기간(`from`~`to`)을 월 단위로 직접 변환한다(설계 §2 자율 결정 5,
 * 기존 `cycleExportRangeFrom(cycleMonths)`을 대체 — 이 페이지는 더 이상 `/settlements/revenue`를
 * 호출하지 않아 `cycleMonths`를 얻을 방법이 없다).
 */
export function csvExportRangeFromFilter(
  filter: Pick<RevenuePeriod, 'from' | 'to'>
): { fromMonth: string; toMonth: string } {
  return { fromMonth: filter.from.slice(0, 7), toMonth: filter.to.slice(0, 7) }
}
